<template>
  <div class="container-fluid">
    <div>
      <div class="d-flex justify-content-between flex-column flex-md-row align-items-stretch align-items-md-center gap-2 gt-model-display-header">
        <h2 v-if="displayPageTitle" class="mb-0">
          {{ displayPageTitle }}
        </h2>
        <div class="d-flex align-items-center gap-2 flex-wrap gt-model-display-actions">
          <DemandaJurisprudenciaButton
            v-if="item && selectedModel"
            :selected-model="selectedModel"
            :item="item"
            :can-access-jurisprudencia="canAccessJurisprudenciaModule"
            @referencias-guardadas="onJurisprudenciaReferenciasGuardadas"
          />
          <div class="btn-group me-2" v-if="itemRecordHref">
            <router-link
              class="btn btn-sm btn-outline-primary px-4 gt-record-link"
              :to="itemRecordHref"
            >
              <i class="material-icons align-middle" style="font-size: 1rem">visibility</i>
              <span class="ms-1">{{ $t("templatePages.openRecord") }}</span>
            </router-link>
          </div>
          <div class="btn-group me-2" v-if="canEditModel">
            <button class="btn btn-sm btn-primary px-4" @click="editItem()">
              Editar
            </button>
          </div>
        
          <button
            v-if="showSendByEmailButton"
            class="btn btn-sm btn-primary px-4"
            :disabled="generatingPdfForEmail"
            @click="openEmailModal"
          >
            <i class="material-icons align-middle" style="font-size: 1rem;">email</i>
            {{ generatingPdfForEmail ? ($t("Generating PDF...") || "Generating PDF...") : ($t("Send by email") || "Send by email") }}
          </button>
        </div>
      </div>
      <div v-if="selectedModel && item && modelFields">
        <ShippingIncoherenceBanner
          v-if="item._id"
          :model-name="selectedModel"
          :item-id="String(item._id)"
          :reload-key="item.updatedAt || ''"
        />
        <div>
          <div class="px-2" ref="displayContentForPdf">
            <ContentSkeleton v-if="!selectedView" variant="detail" :rows="8" padded />
            <ViewHtmlContent
              v-else-if="selectedView.type === 'viewhtml'"
              ref="viewHtmlContent"
              :view="selectedView"
              :value="item"
              :fields="modelFields"
              :lists="lists"
              :models="models"
            />
            <ViewDesignContent
              v-else-if="selectedView.design && selectedView.design.containers"
              :fields="modelFields"
              :value="item"
              :visualGroups="selectedModelData.visualGroups"
              :lists="lists"
              :models="models"
              :selectedModel="selectedModel"
              :view="selectedView"
            />
            <ViewContent
              v-else
              :fields="modelFields"
              :value="item"
              :visualGroups="selectedModelData.visualGroups"
              :lists="lists"
              :models="models"
              :selectedModel="selectedModel"
              :view="selectedView"
            />
          </div>
        </div>
      </div>
      <ContentSkeleton v-else variant="detail" :rows="8" padded />
    </div>

    <EmailTemplateSender
      v-if="showEmailModal"
      document-type="display"
      :document-id="$route.params.itemId"
      :model-name="selectedModel"
      :view-id="$route.params.viewId"
      :pdf-base64="emailPdfBase64"
      :pdf-file-name="emailPdfFileName"
      @close="onCloseEmailModal"
    />
  </div>
</template>

<script>
import ViewContent from "@/components/gotrading/common/ViewContent.vue";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";
import ViewDesignContent from "@/components/gotrading/common/ViewDesignContent.vue";
import ViewHtmlContent from "@/components/gotrading/common/ViewHtmlContent.vue";
import Form from "@/components/gotrading/common/Form.vue";
import Breadcrumb from "@/components/gotrading/common/Breadcrumb.vue";
import EmailTemplateSender from "@/components/email/EmailTemplateSender.vue";
import { wencesApi } from "@/api/wencesApi";
import html2pdf from "html2pdf.js";
import exportMixin from "@/mixins/exportMixin";
import permissionsMixin from "@/mixins/permissionsMixin";
import ShippingIncoherenceBanner from "@/components/gotrading/common/ShippingIncoherenceBanner.vue";
import DemandaJurisprudenciaButton from "@/modules/jurisprudencia_module/components/DemandaJurisprudenciaButton.vue";
import { buildDynamicModelItemViewPath } from "@/utils/dynamicModelPaths";

export default {
  name: "DynamicModelDisplay",
  mixins: [exportMixin, permissionsMixin],
  inject: {
    dashboardLayout: { default: null },
  },
  components: {
    ContentSkeleton,
    ViewContent,
    ViewDesignContent,
    ViewHtmlContent,
    Breadcrumb,
    Form,
    EmailTemplateSender,
    DemandaJurisprudenciaButton,
    ShippingIncoherenceBanner,
  },
  data() {
    return {
      models: [],
      selectedModel: "",
      selectedModelData: null,
      modelFields: [],
      item: null,
      lists: [],
      referenceItems: {},
      modelViews: [],
      selectedView: null,
      templatePdfEnabled: false,
      showEmailModal: false,
      generatingPdfForEmail: false,
      emailPdfBase64: null,
      emailPdfFileName: null,
    };
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.initializeData(); // Tu método de inicialización
    });
  },
  watch: {
    $route: {
      immediate: true,
      handler(to, from) {
        this.initializeData(); // Tu método de inicialización
      },
    },
    "$route.params.modelName": {
      immediate: true,
      handler(newVal) {
        if (newVal && this.models.length) {
          const found = this.models.find(
            (m) => m.modelName.toLowerCase() === newVal.toLowerCase()
          );
          if (found) {
            this.selectedModel = found.modelName;
            this.fetchFields();
          }
        }
      },
    },
    "$route.params.itemId": {
      immediate: true,
      handler(newVal) {
        if (newVal && this.selectedModel) {
          this.fetchItem(newVal);
        }
      },
    },
    "$route.params.viewId": {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.fetchView(newVal);
        }
      },
    },
    models: {
      immediate: true,
      handler(newVal) {
        const modelName = this.$route.params.modelName;
        if (modelName && newVal.length) {
          const found = newVal.find(
            (m) => m.modelName.toLowerCase() === modelName.toLowerCase()
          );
          if (found) {
            this.selectedModel = found.modelName;
            this.fetchFields();
          }
        }
      },
    },
    selectedModel: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.fetchModelViews();
        }
      },
    },
    displayPageTitle: {
      immediate: true,
      handler(title) {
        if (!title) return;
        const siteName =
          (this.dashboardLayout &&
            this.dashboardLayout.site &&
            this.dashboardLayout.site.siteName) ||
          "";
        document.title = siteName ? `${title} | ${siteName}` : title;
      },
    },
  },
  computed: {
    displayPageTitle() {
      const viewTitle =
        (this.selectedView &&
          (this.selectedView.label || this.selectedView.name)) ||
        "";
      if (viewTitle) return viewTitle;
      if (!this.selectedModelData) return "";
      const modelLabel = this.selectedModelData.label
        ? this.$t(this.selectedModelData.label)
        : this.$t(this.selectedModelData.modelName);
      return this.item ? `${modelLabel}-${this.item.idModel}` : modelLabel;
    },
    canEditModel() {
      if (!this.selectedModel) return false;
      const modelNameLower = this.selectedModel.toLowerCase();
      return this.hasPermission(`${modelNameLower}.manage`);
    },
    /** Ficha del ítem al que pertenece esta vista (el shipping, la proforma, etc.). */
    itemRecordHref() {
      const itemId =
        this.$route.params.itemId || (this.item && this.item._id) || "";
      if (!this.selectedModel || !itemId) return "";
      const title =
        this.item && (this.item.name || this.item.title)
          ? String(this.item.name || this.item.title)
          : "";
      return buildDynamicModelItemViewPath(
        this.selectedModel,
        String(itemId),
        title
      );
    },
    showSendByEmailButton() {
      return this.selectedView && this.templatePdfEnabled;
    },
    canAccessJurisprudenciaModule() {
      const d = this.dashboardLayout;
      return d && typeof d.canAccessJurisprudenciaModule === "boolean"
        ? d.canAccessJurisprudenciaModule
        : false;
    },
  },
  async mounted() {
    await this.initializeData();
    this.checkTemplatePdfEnabled();
  },
  methods: {
    async checkTemplatePdfEnabled() {
      try {
        const res = await wencesApi.get("/api/emails/config/smart-email-enabled");
        if (res.data?.success && res.data?.data) {
          this.templatePdfEnabled = res.data.data.templatePdfEnabled === true;
        } else {
          this.templatePdfEnabled = false;
        }
      } catch (e) {
        this.templatePdfEnabled = false;
      }
    },
    async openEmailModal() {
      if (this.generatingPdfForEmail) return;
      this.generatingPdfForEmail = true;
      this.$root.$emit("show-loading", this.$t("Generating PDF...") || "Generating PDF...");
      try {
        // Viewhtml: use ViewHtmlContent PDF (getPdfForEmail). Otherwise: generate from DOM (displayContentForPdf).
        if (this.selectedView?.type === "viewhtml" && this.$refs.viewHtmlContent?.getPdfForEmail) {
          const { base64, fileName } = await this.$refs.viewHtmlContent.getPdfForEmail();
          this.emailPdfBase64 = base64;
          this.emailPdfFileName = fileName;
          this.showEmailModal = true;
        } else if (this.$refs.displayContentForPdf) {
          const el = this.$refs.displayContentForPdf;
          const opt = {
            margin: 0,
            filename: `display-${this.selectedModel}-${this.$route.params.itemId}.pdf`,
            image: { type: "jpeg", quality: 0.92 },
            html2canvas: {
              scale: 2,
              useCORS: true,
              allowTaint: true,
              backgroundColor: "#ffffff",
              letterRendering: true,
              logging: false,
            },
            jsPDF: { unit: "cm", format: "a4", orientation: "portrait" },
          };
          const pdfBlob = await html2pdf().set(opt).from(el).outputPdf("blob");
          const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result).replace(/^data:application\/pdf;base64,/, ""));
            reader.onerror = reject;
            reader.readAsDataURL(pdfBlob);
          });
          this.emailPdfBase64 = base64;
          this.emailPdfFileName = `display-${this.selectedModel}-${this.$route.params.itemId}.pdf`;
          this.showEmailModal = true;
        } else {
          this.$root.$emit("show-error", this.$t("No content to export") || "No content to export");
        }
      } catch (err) {
        console.error("Error generating PDF:", err);
        this.$root.$emit(
          "show-error",
          err.response?.data?.message || err.message || (this.$t("Failed to generate PDF. Please try again.") || "Failed to generate PDF. Please try again.")
        );
      } finally {
        this.generatingPdfForEmail = false;
        this.$root.$emit("hide-loading");
      }
    },
    onCloseEmailModal() {
      this.showEmailModal = false;
      this.emailPdfBase64 = null;
      this.emailPdfFileName = null;
    },
    async initializeData() {
      await this.fetchModels();
      await this.fetchLists();
      await this.fetchModelViews();
    },
    async fetchModels() {
      const { data } = await wencesApi.get("/api/gotrading/model-definitions");
      this.models = data;
    },
    async fetchFields() {
      this.modelFields = [];
      if (!this.selectedModel) return;

      const model = this.models.find(
        (m) => m.modelName.toLowerCase() === this.selectedModel.toLowerCase()
      );

      if (model) {
        this.selectedModelData = model;
        this.modelFields = model.fields;

        // Fetch the item if we have an itemId
        if (this.$route.params.itemId) {
          this.fetchItem(this.$route.params.itemId);
        }
      }
    },
    async fetchItem(itemId) {
      try {
        const { data } = await wencesApi.get(
          `/api/gotrading/dynamic/${this.selectedModel}/${itemId}`
        );

        // Procesar los datos para asegurar que los campos de la vista tengan valores
        const processedData = { ...data };

        // Si tenemos una vista seleccionada, asegurarnos de que todos sus campos existan en los datos
        if (this.selectedView && this.selectedView.fields) {
          this.selectedView.fields.forEach((field) => {
            if (processedData[field.key] === undefined) {
              processedData[field.key] = "";
            }
          });
        }

        this.item = processedData;
      } catch (error) {
        console.error("Error loading item:", error);
      }
    },
    async fetchLists() {
      const { data } = await wencesApi.get("/api/gotrading/lists");
      this.lists = data;
    },
    async fetchView(viewId) {
      try {
        const { data } = await wencesApi.get(`/api/gotrading/views/${viewId}`);
        this.selectedView = data;
        if (data) this.checkTemplatePdfEnabled();

        // Si la vista define campos concretos, filtrar; lista vacía = usar todos (viewhtml)
        const viewFields = this.selectedView?.fields;
        if (
          this.selectedView &&
          Array.isArray(viewFields) &&
          viewFields.length > 0 &&
          this.modelFields.length
        ) {
          const viewFieldKeys = viewFields.map((f) => f.key);
          this.modelFields = this.modelFields.filter((field) =>
            viewFieldKeys.includes(field.key)
          );
        }
      } catch (error) {
        console.error("Error loading view:", error);
      }
    },
    editItem() {
      // Cuando venimos de una vista concreta (viewId en la ruta, p.ej. el PDF de
      // comprador/vendedor), usar DynamicModelEditWithView para que el viewId viaje
      // en la URL y el editor pueda volver a esa misma vista al guardar/cancelar.
      // "DynamicModelEdit" no tiene :viewId en su path, así que pasarlo como param
      // no lo incluye en la URL y se pierde silenciosamente.
      const viewId = this.$route.params.viewId;
      this.$router.push({
        name: viewId ? "DynamicModelEditWithView" : "DynamicModelEdit",
        params: {
          modelName: this.selectedModel,
          itemId: this.$route.params.itemId,
          ...(viewId ? { viewId } : {}),
        },
      });
    },
    onJurisprudenciaReferenciasGuardadas() {
      const id = this.$route.params.itemId;
      if (id && this.selectedModel) {
        this.fetchItem(id);
      }
    },
    async fetchModelViews() {
      try {
        const { data } = await wencesApi.get("/api/gotrading/views");
        this.modelViews = data.filter(
          (view) => view.modelName === this.selectedModel
        );
      } catch (error) {
        console.error("Error loading model views:", error);
      }
    },
    goToView(view) {
      if (view.type === "report") {
        this.$router.push(
          `/dynamic-model/${view.modelName}/report/${view._id}`
        );
      } else if (view.type === "combined_view") {
        this.$router.push(
          `/dynamic-model/${view.modelName}/combined/${view._id}`
        );
      } else {
        this.$router.push(
          `/dynamic-model/${view.modelName}/display/${this.$route.params.itemId}/${view._id}`
        );
      }
    },
  },
};
</script>

<style scoped>
.card {
  margin-top: 1rem;
}

/* El tema fuerza `a:hover{color: … !important}` y pisaría el color del botón. */
.gt-record-link.btn:hover,
.gt-record-link.btn:focus {
  color: var(--bs-btn-hover-color) !important;
}

.btn-group {
  margin-left: 0.5rem;
}

.btn-group .btn {
  margin-right: 0.25rem;
}

.btn-group .btn.active {
  background-color: #007bff;
  color: white;
}
</style>
